// The wall: every achievement on the account, family by family
// (ACHIEVEMENTS.md §11; BACKLOG A4; scenario R4). `GET /api/me/achievements`.
//
// A LIST SCREEN (TEMPLATES.md): a pinned filter bar, then one titled `Panel`
// per family — the site's own families first, then each sport the person has
// played under its own heading. In each family: found (newest first), open
// (closest to done first), hints, then one tile for the family's secrets.
//
// THE FILTER IS IN THE URL (`?show=found|try|mysteries`, `?family=<id>`), so
// a link can open on To try, and `?badge=<id or key>` opens one badge: the
// bell's notice for a find lands on it.
//
// BY WIDTH (§11): three badges to a row on a phone with the detail as a bottom
// sheet; five on a tablet with the detail as a drawer; six on a desktop with a
// side column holding the selected badge, or Just found and Closest when none
// is; and at 1920 the families in two columns beside that side column.
//
// AN ACCOUNT WITH NOTHING FOUND opens on To try, so an empty wall shows what
// to try — First steps and Explorer come first.

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { valueOf, type AchievementFamily, type AchievementsView } from '@fsp/domain';
import { featureLink, useAchievements } from '../data/achievements';
import { useLeagueCards } from '../data/LeaguesProvider';
import { useAtLeast } from '../data/detail';
import { Panel } from '../kit/Panel';
import { Button, Callout, Caption, Choice, Line, Segments, Stack, Text, Title } from '../kit/ui';
import { EmptyState } from '../kit';
import { DetailSheet } from '../templates/DetailSheet';
import { useTier } from '../shell/useTier';
import {
  AchievementDetail,
  FoundTile,
  HintTile,
  OpenTile,
  SecretsTile,
  type Opened,
} from './shared/AchievementKit';
import '../styles/screens/achievements.css';

type Show = 'all' | 'found' | 'try' | 'mysteries';
const SHOWS: readonly { id: Show; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'found', label: 'Found' },
  { id: 'try', label: 'To try' },
  { id: 'mysteries', label: 'Mysteries' },
];

/** The phone's family choice for no family in particular; no family's id. */
const EVERY = 'every';

// At 1920 the families sit in two columns, each about half a desktop's list: four fit there.
const COLS = { phone: 3, tablet: 5, desktop: 6, large: 4 } as const;

/** Every badge in the view, by what `?badge=` names: a found or open one's id, a hint's key. */
function openedOf(view: AchievementsView, badge: string | null): Opened | null {
  if (badge === null) return null;
  for (const f of view.families) {
    const base = { familyName: f.name, familyId: f.id };
    const found = f.found.find((a) => a.id === badge);
    if (found) return { kind: 'found', a: found, ...base };
    const open = f.open.find((a) => a.id === badge);
    if (open) return { kind: 'open', a: open, ...base };
    const hint = f.hints.find((a) => a.key === badge);
    if (hint) return { kind: 'hint', a: hint, ...base };
  }
  return null;
}

/** A family as the filter leaves it. */
function shown(f: AchievementFamily, show: Show): AchievementFamily {
  return {
    ...f,
    found: show === 'try' || show === 'mysteries' ? [] : f.found,
    open: show === 'found' || show === 'mysteries' ? [] : f.open,
    hints: show === 'found' || show === 'try' ? [] : f.hints,
    secrets: show === 'found' || show === 'try' ? 0 : f.secrets,
  };
}

const isEmpty = (f: AchievementFamily) =>
  f.found.length + f.open.length + f.hints.length + f.secrets === 0;

export function AchievementsScreen() {
  const read = useAchievements();
  const view = valueOf(read.snapshot);
  const tier = useTier();
  const counts = useAtLeast('standard');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { cards } = useLeagueCards();
  const [allRecent, setAllRecent] = useState(false);
  const firstLeague = cards[0]?.league.id ?? null;

  const asked = params.get('show');
  // NOTHING FOUND YET opens on To try (§11).
  const show: Show = SHOWS.some((s) => s.id === asked)
    ? (asked as Show)
    : view !== null && view.summary.found === 0
      ? 'try'
      : 'all';
  const familyId = params.get('family');
  const badge = params.get('badge');
  const wide = tier === 'desktop' || tier === 'large';

  const setParam = (key: string, value: string | null) =>
    setParams(
      (p) => {
        const next = new URLSearchParams(p);
        if (value === null) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  // ON A PHONE OR TABLET THE BADGE IS A SHEET, NOT AN ADDRESS (B-308). The
  // sheet holds a history entry of its own while it is open, so Back closes
  // it; held in the address as well, closing it left an entry that opened it
  // again on the next Back. `?badge=` still opens one from a link (Account, a
  // notice), and is taken out of the address as it does. A desktop shows the
  // badge in its side column, and the address keeps it there.
  const [sheetBadge, setSheetBadge] = useState<string | null>(null);
  useEffect(() => {
    if (wide || badge === null) return;
    setSheetBadge(badge);
    setParam('badge', null);
  }, [wide, badge]);
  const chosen = wide ? badge : sheetBadge;
  const opened = view === null ? null : openedOf(view, chosen);
  const open = (id: string) => (wide ? setParam('badge', id) : setSheetBadge(id));
  const close = () => (wide ? setParam('badge', null) : setSheetBadge(null));

  const families = useMemo(
    () =>
      (view?.families ?? [])
        .filter((f) => familyId === null || f.id === familyId)
        .map((f) => shown(f, show))
        .filter((f) => !isEmpty(f)),
    [view, familyId, show],
  );

  const link =
    opened?.kind === 'open' && opened.a.feature !== null
      ? (() => {
          const l = featureLink(opened.a.feature, firstLeague);
          return { label: l.label, onGo: () => navigate(l.to) };
        })()
      : null;

  if (view === null) {
    return (
      <div className="fsp-screen">
        <div className="fsp-pinned">
          <Stack gap={2}>
            <Title>Achievements</Title>
            {read.snapshot.state === 'failed' ? (
              <Callout tone="bad">
                <Stack gap={2}>
                  <span>{read.snapshot.failure.message}</span>
                  <Line>
                    <Button size="xs" onClick={read.reload}>
                      Try again
                    </Button>
                  </Line>
                </Stack>
              </Callout>
            ) : (
              <Text tone="secondary">Reading your achievements…</Text>
            )}
          </Stack>
        </div>
      </div>
    );
  }

  const tiles = (f: AchievementFamily): ReactNode[] => [
    ...f.found.map((a) => (
      <FoundTile
        key={a.id}
        a={a}
        family={f.id}
        selected={chosen === a.id}
        onOpen={() => open(a.id)}
      />
    )),
    ...f.open.map((a) => (
      <OpenTile
        key={a.id}
        a={a}
        family={f.id}
        selected={chosen === a.id}
        onOpen={() => open(a.id)}
      />
    )),
    ...f.hints.map((a) => (
      <HintTile
        key={a.key}
        a={a}
        family={f.id}
        selected={chosen === a.key}
        onOpen={() => open(a.key)}
      />
    )),
    ...(f.secrets > 0 ? [<SecretsTile key="secrets" family={f.id} count={f.secrets} />] : []),
  ];

  const grid = (children: ReactNode, cols: number = COLS[tier]) => (
    <div className="fsp-ach-grid" data-cols={cols}>
      {children}
    </div>
  );

  // JUST FOUND: the last fortnight (§11) — in the side column on a desktop, so
  // not twice. WRAPPED LIKE EVERY FAMILY, never a row that scrolls sideways
  // (no page scrolls sideways), and two rows of it until asked for the rest, so a
  // launch day's finds do not push the families off the screen.
  const recent =
    !wide && (show === 'all' || show === 'found')
      ? view.recent.filter((a) => familyId === null || a.family === familyId)
      : [];
  const firstRows = COLS[tier] * 2;
  const recentShown = allRecent ? recent : recent.slice(0, firstRows);
  const recentStrip =
    recent.length > 0 ? (
      <Panel title="Just found" bodyClass="fsp-ach-body">
        <Stack gap={2}>
          {grid(
            recentShown.map((a) => (
              <FoundTile
                key={a.id}
                a={a}
                family={a.family}
                selected={chosen === a.id}
                onOpen={() => open(a.id)}
              />
            )),
          )}
          {recent.length > firstRows ? (
            <Line>
              <Button size="xs" variant="quiet" onClick={() => setAllRecent((v) => !v)}>
                {allRecent ? 'Show fewer' : 'Show the rest'}
              </Button>
            </Line>
          ) : null}
        </Stack>
      </Panel>
    ) : null;

  // THE SPORTS' FAMILIES UNDER THEIR NAMES (§11): a heading before each sport's first family.
  const body: ReactNode[] = [];
  let sport: string | null = null;
  for (const f of families) {
    if (f.sport !== null && f.sport !== sport) {
      sport = f.sport;
      body.push(
        <h2 key={`sport-${f.sport}`} className="fsp-ach-sport">
          {f.sport}
        </h2>,
      );
    }
    body.push(
      <Panel
        key={f.id}
        title={f.name}
        bodyClass="fsp-ach-body"
        meta={counts && f.found.length > 0 ? <Caption>{f.found.length} found</Caption> : undefined}
      >
        {grid(tiles(f))}
      </Panel>,
    );
  }

  // CLOSEST: the open ones nearest done, for the side column when nothing is selected.
  const closest = view.families
    .flatMap((f) => f.open.map((a) => ({ a, f })))
    .filter((x) => x.a.progress !== null && x.a.progress.have > 0)
    .sort(
      (x, y) => y.a.progress!.have / y.a.progress!.need - x.a.progress!.have / x.a.progress!.need,
    )
    .slice(0, 3);

  const side = wide ? (
    <section className="fsp-region fsp-region-plain" aria-label="Side">
      <div className="fsp-ach-side">
        {opened !== null ? (
          <Panel
            title={opened.familyName}
            bodyClass="fsp-ach-body"
            meta={
              <Button size="xs" variant="quiet" onClick={close}>
                Close
              </Button>
            }
          >
            <AchievementDetail opened={opened} link={link} />
          </Panel>
        ) : (
          <>
            {view.recent.length > 0 ? (
              <Panel title="Just found" bodyClass="fsp-ach-body">
                {grid(
                  view.recent
                    .slice(0, 6)
                    .map((a) => (
                      <FoundTile key={a.id} a={a} family={a.family} onOpen={() => open(a.id)} />
                    )),
                  3,
                )}
              </Panel>
            ) : null}
            <Panel title="Closest" bodyClass="fsp-ach-body">
              {closest.length > 0 ? (
                grid(
                  closest.map(({ a, f }) => (
                    <OpenTile key={a.id} a={a} family={f.id} onOpen={() => open(a.id)} />
                  )),
                  3,
                )
              ) : (
                <Text tone="secondary" size="xs" block>
                  Nothing under way yet. Tap an open one to see where it is tried.
                </Text>
              )}
            </Panel>
          </>
        )}
      </div>
    </section>
  ) : null;

  // THE FAMILIES, as chips that wrap — never a row that scrolls sideways (Ken,
  // 2026-10-04) — and on a phone as one choice, since a dozen wrapped chips
  // would fill a pinned header the wall has to scroll beneath.
  const familyChips =
    tier === 'phone' ? (
      <Choice
        label="Family"
        hideLabel
        searchable={false}
        required
        options={[
          { value: EVERY, label: 'Every family' },
          ...view.families.map((f) => ({ value: f.id, label: f.name })),
        ]}
        value={familyId ?? EVERY}
        onChange={(v) => setParam('family', v === null || v === EVERY ? null : v)}
      />
    ) : (
      <div className="fsp-ach-chips" role="group" aria-label="Families">
        <button
          type="button"
          className="fsp-ach-chip"
          aria-pressed={familyId === null}
          onClick={() => setParam('family', null)}
        >
          Every family
        </button>
        {view.families.map((f) => (
          <button
            key={f.id}
            type="button"
            className="fsp-ach-chip"
            aria-pressed={familyId === f.id}
            onClick={() => setParam('family', familyId === f.id ? null : f.id)}
          >
            {f.name}
          </button>
        ))}
      </div>
    );

  return (
    <div className="fsp-screen">
      <div className="fsp-pinned">
        <div className="fsp-ach-filters">
          <Line spread>
            <div className="fsp-ach-head">
              <Title>Achievements</Title>
              {/* A paragraph, not a row's second line: it wraps (UX-10). */}
              <div className="fsp-sub fsp-ui-wrap-text">
                Found on your account, once each, across every league.
                {counts ? ` ${view.summary.found} found.` : ''}
              </div>
            </div>
          </Line>
          <Segments<Show>
            items={SHOWS}
            value={show}
            // `all` is written out: an account with nothing found opens on To
            // try when the address names nothing, so All has to be said.
            onChange={(id) => setParam('show', id)}
            label="Which achievements"
          />
          {familyChips}
        </div>
      </div>
      <div className={`fsp-regions ${side ? 'fsp-list-with-side' : 'fsp-list-alone'}`}>
        <section className="fsp-region fsp-region-plain" aria-label="Achievements">
          <div className="fsp-ach-wall">
            {recentStrip}
            {families.length === 0 ? (
              <EmptyState>
                {show === 'found'
                  ? 'Nothing found here yet. The open ones say how to start.'
                  : 'Nothing here with these filters.'}
              </EmptyState>
            ) : (
              <div className="fsp-ach-families" data-two={tier === 'large' ? '' : undefined}>
                {body}
              </div>
            )}
          </div>
        </section>
        {side}
      </div>
      {/* PHONE AND TABLET: the detail slides in — a bottom sheet on a phone, a
          drawer on a tablet (`DetailSheet`). A desktop shows it in the side column. */}
      {!wide ? (
        <DetailSheet opened={opened !== null} onClose={close} title={opened?.familyName ?? ''}>
          {opened !== null ? <AchievementDetail opened={opened} link={link} /> : null}
        </DetailSheet>
      ) : null}
    </div>
  );
}
